import { fabric } from 'fabric';
import { Canvas, StaticCanvas } from 'fabric/fabric-impl';
import { ObservableField } from 'shared/utils';
import customization from '../addons/customization';
import { Resolution, Scheme } from '../types';

interface IOptionsResizeByScreen {
  resolution?: Resolution;
  sizeOffset?: Resolution;
}

export default class BaseFabric {
  canvas!: StaticCanvas | Canvas;

  template?: Scheme;
  resolution: ObservableField<Resolution> = new ObservableField({ width: 1920, height: 1080 });
  deletedObjects?: { background: string };
  cursorPos?: { x: number; y: number };

  clear(safeBackground = false) {
    const backgroundColor = this.canvas.backgroundColor;
    this.canvas.clear();

    if (!safeBackground) return;
    this.canvas.backgroundColor = backgroundColor;
    this.canvas.requestRenderAll();
  }

  normalizedViewport(offset?: { x?: number; y?: number; zoom?: number }) {
    const savedViewport = this.resolution;
    const zoom = Math.min(
      (this.canvas.getHeight() / savedViewport.value.height) * (offset?.zoom ?? 1),
      (this.canvas.getWidth() / savedViewport.value.width) * (offset?.zoom ?? 1),
    );
    this.canvas.setZoom(zoom * (offset?.zoom ?? 1));

    const viewport = this.canvas.viewportTransform;
    if (!viewport) return;
    viewport[4] = (this.canvas.getWidth() - (savedViewport.value.width - (offset?.x ?? 0)) * zoom) / 2;
    viewport[5] = (this.canvas.getHeight() - (savedViewport.value.height - (offset?.y ?? 0)) * zoom) / 2;
    this.canvas.setViewportTransform(viewport);
    this.canvas.requestRenderAll();
  }

  hideBackground() {
    this.deletedObjects = { background: this.canvas.backgroundColor?.toString() ?? 'transparent' };
    this.canvas.backgroundColor = 'transparent';
    this.canvas.requestRenderAll();
  }

  showBackground() {
    if (!this.deletedObjects) return;
    this.canvas.backgroundColor = this.deletedObjects.background;
    this.canvas.requestRenderAll();
  }

  loadLocal({ scheme, safeBackground }: { safeBackground?: boolean; scheme: Scheme | string }) {
    const oldBackground = this.canvas.backgroundColor;
    return new Promise((resolve) => {
      if (!scheme) return resolve(false);
      const schemeParse: Scheme = typeof scheme === 'string' ? JSON.parse(scheme) : scheme;
      fabric.util.enlivenObjects(
        schemeParse.objects ?? [],
        (objs: fabric.Object[]) => {
          this.canvas.clear();
          this.canvas.add(...objs);
          this.canvas.getObjects().forEach((obj) => obj.set({ ...customization.getAllProperties() }));
          if (safeBackground) this.canvas.backgroundColor = oldBackground;
          // this.canvas.requestRenderAll()
          resolve(true);
        },
        'fabric',
      );
    });
  }

  getScheme(hideBackgroundElems = false): Scheme {
    hideBackgroundElems && this.hideBackground();
    this.canvas.includeDefaultValues = false;
    const template = this.canvas.toDatalessJSON([
      'selectable',
      'data',
      'lockMovementX',
      'lockMovementY',
      'lockScalingX',
      'lockScalingY',
      'lockUniScaling',
      'lockRotation',
      'lockSkewingX',
      'lockSkewingY',
      'lockScalingFlip',
    ]);
    hideBackgroundElems && this.showBackground();
    return template;
  }

  setResolution(resolution: Resolution) {
    this.resolution.value = resolution;
  }

  setDimensions({ width, height }: Resolution) {
    this.canvas.setDimensions({ width, height });
    this.canvas.requestRenderAll();
  }

  getParentSize() {
    const { parentElement } = this.canvas.getElement();
    if (!parentElement) return;
    const height = parentElement.clientHeight;
    const width = parentElement.clientWidth;
    return { width, height };
  }

  setParentSize(offset?: { x?: number; y?: number }) {
    const { width, height } = this.getParentSize() ?? {};
    if (width === undefined || height === undefined) return;
    this.canvas.setWidth(width + (offset?.x ?? 0));
    this.canvas.setHeight(height + (offset?.y ?? 0));
    this.canvas.requestRenderAll();
  }

  render() {
    this.canvas.renderAll();
  }

  getCanvasElement() {
    return this.canvas.getElement();
  }

  resizeByScreen(options?: IOptionsResizeByScreen) {
    const { resolution = this.resolution.value, sizeOffset = { width: 0, height: 0 } } = options ?? {};

    const w = window,
      d = document,
      docElems = d.documentElement,
      body = d.getElementsByTagName('body')[0],
      pageX = (w.innerWidth || docElems.clientWidth || body.clientWidth) - sizeOffset.width,
      pageY = (w.innerHeight || docElems.clientHeight || body.clientHeight) - sizeOffset.height;

    const canvas = this.getCanvasElement();
    if (!canvas) return;

    const resolutionX = resolution.width ?? 1920;
    const resolutionY = resolution.height ?? 1080;

    let canvasX, canvasY;
    if (resolutionX / resolutionY <= pageX / pageY) {
      const maxFactor = Math.max(resolutionX / resolutionY, resolutionY / resolutionX);
      canvasY = pageY;
      canvasX = pageY * maxFactor;
    } else {
      const minFactor = Math.min(resolutionX / resolutionY, resolutionY / resolutionX);
      canvasX = pageX;
      canvasY = canvasX * minFactor;
    }

    const width = `width:${canvasX}px;`;
    const height = `height:${canvasY}px;`;
    canvas.setAttribute('style', width + height);
  }

  protected init() {
    this.canvas.on('mouse:move', (e) => {
      this.cursorPos = e.absolutePointer;
    });

    fabric.Object.NUM_FRACTION_DIGITS = 10;
  }
}
